import { useState } from 'react';
import { Bluetooth, CheckCircle2, Star, Trash2 } from 'lucide-react';
import { api, ApiClientError } from '../../lib/api.js';
import { useBluetoothPrinter } from '../../hooks/useBluetoothPrinter.js';
import { Button } from '../../components/ui/Button.jsx';
import { TextField, SelectField } from '../../components/ui/Field.jsx';

const PAPER_LABEL = { 58: '58mm thermal', 80: '80mm thermal', a4: 'A4' };

function PrinterRow({ printer, onSetDefault, onDelete, onTest, busyAction }) {
  const busy = busyAction?.id === printer.id ? busyAction.action : null;

  return (
    <div className="flex flex-col gap-2 rounded-card border border-border p-3.5 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <div className="flex items-center gap-2 font-medium">
          {printer.name}
          {printer.is_default ? (
            <span className="flex items-center gap-1 rounded-full border border-primary/30 px-2 py-0.5 text-xs text-primary">
              <Star size={12} aria-hidden="true" /> Default
            </span>
          ) : null}
        </div>
        <p className="text-sm text-muted">
          {PAPER_LABEL[printer.paper_width] ?? printer.paper_width}
          {printer.device_name ? ` · ${printer.device_name}` : ''}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" loading={busy === 'test'} onClick={() => onTest(printer)}>
          Test print
        </Button>
        {!printer.is_default ? (
          <Button variant="secondary" size="sm" loading={busy === 'default'} onClick={() => onSetDefault(printer)}>
            Set default
          </Button>
        ) : null}
        <Button variant="ghost" size="sm" loading={busy === 'delete'} onClick={() => onDelete(printer)}>
          <Trash2 size={16} aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

export function PrintersSection() {
  const bt = useBluetoothPrinter();
  const [pairName, setPairName] = useState('');
  const [pairWidth, setPairWidth] = useState('58');
  const [pairError, setPairError] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [testStatus, setTestStatus] = useState(null);

  const runBusy = async (printer, action, fn) => {
    setBusyAction({ id: printer.id, action });
    try {
      await fn();
    } finally {
      setBusyAction(null);
    }
  };

  const handlePair = async () => {
    setPairError(null);
    try {
      await bt.pairNewPrinter({ name: pairName.trim(), paperWidth: pairWidth });
      setPairName('');
    } catch (err) {
      setPairError(err instanceof ApiClientError ? err.message : err.message || 'Could not pair that printer.');
    }
  };

  const handleTest = (printer) =>
    runBusy(printer, 'test', async () => {
      setTestStatus(null);
      try {
        const res = await api.raw('/printers/test', { query: { width: printer.paper_width === '80' ? '80' : '58' } });
        const bytes = await res.arrayBuffer();
        await bt.print(bytes, printer);
        setTestStatus({ id: printer.id, ok: true });
      } catch (err) {
        setTestStatus({ id: printer.id, ok: false, message: err.message });
      }
    });

  const handleSetDefault = (printer) => runBusy(printer, 'default', () => bt.updatePrinter(printer.id, { isDefault: true }));
  const handleDelete = (printer) => runBusy(printer, 'delete', () => bt.deletePrinter(printer.id));

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Thermal printers</h2>
        <p className="mt-1 text-sm text-muted">
          Saved Bluetooth thermal printers for this phone. Each device pairs its own browser — set one up here, then
          "Print (thermal)" on any invoice or voucher uses the default.
        </p>
      </div>

      {!bt.isSupported ? (
        <p className="rounded-card border border-warning/30 bg-warning/10 px-3.5 py-3 text-sm text-warning">
          This browser doesn't support Web Bluetooth — that includes every browser on iPhone and iPad, since they all
          run on Safari's engine. Use Chrome on an Android phone to pair a printer.
        </p>
      ) : (
        <>
          {bt.printers.length ? (
            <div className="flex flex-col gap-2">
              {bt.printers.map((printer) => (
                <div key={printer.id} className="flex flex-col gap-1">
                  <PrinterRow
                    printer={printer}
                    onSetDefault={handleSetDefault}
                    onDelete={handleDelete}
                    onTest={handleTest}
                    busyAction={busyAction}
                  />
                  {testStatus?.id === printer.id ? (
                    testStatus.ok ? (
                      <p className="flex items-center gap-1.5 pl-1 text-sm text-success">
                        <CheckCircle2 size={14} aria-hidden="true" /> Sent to printer
                      </p>
                    ) : (
                      <p className="pl-1 text-sm text-danger">{testStatus.message}</p>
                    )
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted">No printers saved yet.</p>
          )}

          <div className="flex flex-col gap-3 rounded-card border border-dashed border-border p-3.5 sm:flex-row sm:items-end">
            <TextField
              label="Name (optional)"
              placeholder="Counter printer"
              value={pairName}
              onChange={(e) => setPairName(e.target.value)}
              className="flex-1"
            />
            <SelectField label="Paper width" value={pairWidth} onChange={(e) => setPairWidth(e.target.value)} className="sm:w-40">
              <option value="58">58mm</option>
              <option value="80">80mm</option>
            </SelectField>
            <Button type="button" onClick={handlePair} loading={bt.isPairing} className="sm:mb-0.5">
              <Bluetooth size={16} aria-hidden="true" /> Pair a new printer
            </Button>
          </div>
          {pairError ? <p className="text-sm text-danger">{pairError}</p> : null}
        </>
      )}
    </section>
  );
}

export default PrintersSection;
